import { useFormik } from 'formik';
import { boolean, date, object, string } from 'yup';
import { i18n } from '~/lib/i18n';
import { duration } from '~/lib/validators/duration';
import type { EventFormValues, EventMutationValues } from '../components/event-form';

const serializedValues = (values: EventFormValues): EventMutationValues => {
  return {
    includeInPlayoutReporting: values.includeInPlayoutReporting,
    name: values.name,
    sponsorStartTime: values.sponsorStartTime || null,
    sponsorStopTime: values.sponsorStopTime || null,
    start: values.start.toISO() ?? '',
    stop: values.stop.toISO() ?? '',
  };
};

const validationSchema = object({
  includeInPlayoutReporting: boolean().required(),
  name: string().required(i18n.t('common.validation.name_required')).label('Name'),
  sponsorStartTime: duration({ min: { minutes: 0 }, max: { hours: 5 } }).optional(),
  sponsorStopTime: duration({ min: { minutes: 0 }, max: { hours: 5 } }).optional(),
  start: date()
    .required(i18n.t('pages.event_types.events_tab.new_event_dialog.validation.start_required'))
    .label('Start Date & Time')
    .typeError(i18n.t('pages.event_types.events_tab.new_event_dialog.validation.start_required')),
  stop: date()
    .required(i18n.t('pages.event_types.events_tab.new_event_dialog.validation.stop_required'))
    .label('Stop Date & Time')
    .typeError(i18n.t('pages.event_types.events_tab.new_event_dialog.validation.stop_required'))
    .when('$start', ([start], schema) => {
      if (start) {
        return schema.test(
          'stop-after-start',
          i18n.t('pages.event_types.events_tab.new_event_dialog.validation.stop_after_start'),
          (stop) => stop > start,
        );
      }
      return schema;
    }),
});

export const useEventFormik = (
  initialValues: EventFormValues,
  onSubmit: (values: EventMutationValues) => Promise<void>,
) => {
  const handleSubmit = async (values: EventFormValues) => {
    await onSubmit(serializedValues(values));
  };

  return useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    onSubmit: handleSubmit,
    validateOnBlur: true,
    validateOnMount: false,
    validationSchema: validationSchema,
  });
};
